import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { apiDRSAudit, DRSAuditEntry } from "@src/apis/drs-audit";

interface ActivityHistoryProps {
  drsSummaryId: number;
}

const SOURCE_LABEL: Record<DRSAuditEntry["sourceTable"], string> = {
  DRSMaterialCostSummary: "Materials",
  DRSMobileAssetCostSummary: "Mobile Assets",
  DRSSubcontractorCostSummary: "Subcontractor",
  DRSLabourCostSummary: "Labour",
};

const OPERATION_COLOR: Record<DRSAuditEntry["operation"], string> = {
  INSERT: "#16a34a",
  UPDATE: "#2563eb",
  DELETE: "#dc2626",
};

const ACTOR_COLORS = ["#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#ec4899", "#14b8a6"];

const PAGE_SIZE = 8;
const COLLAPSED_STORAGE_KEY = "aims.activityHistory.collapsed";

const formatMoney = (raw: string | null) => {
  if (raw === null || raw === undefined) return null;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatRelative = (iso: string) => {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 45) return "just now";
  const minutes = Math.floor(diffSec / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const formatAbsolute = (iso: string) =>
  new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const initialsFromActor = (actor: string | null) => {
  if (!actor) return "·";
  const localPart = actor.split("@")[0];
  const tokens = localPart.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return actor.charAt(0).toUpperCase();
  if (tokens.length === 1) return tokens[0].charAt(0).toUpperCase();
  return (tokens[0].charAt(0) + tokens[tokens.length - 1].charAt(0)).toUpperCase();
};

const displayNameFromActor = (actor: string | null) => {
  if (!actor) return "System";
  const localPart = actor.split("@")[0];
  return localPart
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const colorForActor = (actor: string | null) => {
  if (!actor) return "#94a3b8";
  let hash = 0;
  for (let i = 0; i < actor.length; i += 1) hash = (hash << 5) - hash + actor.charCodeAt(i);
  return ACTOR_COLORS[Math.abs(hash) % ACTOR_COLORS.length];
};

interface CostChange {
  label: string;
  from: string | null;
  to: string | null;
  delta: number | null;
}

const buildChanges = (entry: DRSAuditEntry): CostChange[] => {
  const out: CostChange[] = [];
  const push = (label: string, oldRaw: string | null, newRaw: string | null) => {
    const fromN = oldRaw === null ? null : parseFloat(oldRaw);
    const toN = newRaw === null ? null : parseFloat(newRaw);
    const sameValue =
      fromN !== null && toN !== null && Number.isFinite(fromN) && Number.isFinite(toN) && Math.abs(fromN - toN) < 0.005;
    if (oldRaw === newRaw || sameValue) return;
    const delta = fromN !== null && toN !== null && Number.isFinite(fromN) && Number.isFinite(toN) ? toN - fromN : null;
    out.push({ label, from: formatMoney(oldRaw), to: formatMoney(newRaw), delta });
  };
  push("Actual", entry.oldTotalCost, entry.newTotalCost);
  push("Plan", entry.oldPlanTotalCost, entry.newPlanTotalCost);
  return out;
};

const ActivityHistory: React.FC<ActivityHistoryProps> = ({ drsSummaryId }) => {
  const [entries, setEntries] = useState<DRSAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* localStorage may be disabled — ignore */
      }
      return next;
    });
  };

  const load = React.useCallback(async () => {
    if (!drsSummaryId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiDRSAudit({ drsSummaryId, limit: 200 });
      const list: DRSAuditEntry[] = res?.data?.audit ?? [];
      setEntries(list);
    } catch (e: any) {
      setError(e?.message || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [drsSummaryId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => (expanded ? entries : entries.slice(0, PAGE_SIZE)), [entries, expanded]);
  const hiddenCount = entries.length - visible.length;
  const latest = entries[0];

  return (
    <Section>
      <Header>
        <TitleRow>
          <Dot />
          <Title>Activity</Title>
          {entries.length > 0 && <Count>{entries.length}</Count>}
          {collapsed && latest && (
            <CollapsedHint>
              last update <strong>{formatRelative(latest.changedAt)}</strong>
              {latest.changedBy ? ` by ${displayNameFromActor(latest.changedBy)}` : ""}
            </CollapsedHint>
          )}
        </TitleRow>
        <HeaderActions>
          {!collapsed && (
            <Refresh onClick={load} disabled={loading} title="Refresh">
              {loading ? "Loading…" : "Refresh"}
            </Refresh>
          )}
          <Toggle
            onClick={toggleCollapsed}
            title={collapsed ? "Expand activity" : "Collapse activity"}
            aria-expanded={!collapsed}
          >
            <Chevron $collapsed={collapsed}>▾</Chevron>
          </Toggle>
        </HeaderActions>
      </Header>

      {!collapsed && error && <ErrorBox>{error}</ErrorBox>}

      {!collapsed && !loading && entries.length === 0 && !error && (
        <Empty>No changes recorded for this shift yet.</Empty>
      )}

      {!collapsed && (
      <Timeline>
        {visible.map((entry) => {
          const changes = buildChanges(entry);
          const actorColor = colorForActor(entry.changedBy);
          return (
            <Row key={entry.id}>
              <AvatarColumn>
                <Avatar style={{ background: actorColor }}>{initialsFromActor(entry.changedBy)}</Avatar>
              </AvatarColumn>
              <Body>
                <BodyHeader>
                  <Actor>{displayNameFromActor(entry.changedBy)}</Actor>
                  <OpBadge style={{ color: OPERATION_COLOR[entry.operation], borderColor: OPERATION_COLOR[entry.operation] }}>
                    {entry.operation}
                  </OpBadge>
                  <SourceChip>
                    {SOURCE_LABEL[entry.sourceTable] || entry.sourceTable}
                    {entry.workType ? ` · ${entry.workType}` : ""}
                  </SourceChip>
                  <Spacer />
                  <When title={formatAbsolute(entry.changedAt)}>{formatRelative(entry.changedAt)}</When>
                </BodyHeader>
                {changes.length > 0 ? (
                  <Changes>
                    {changes.map((c, i) => (
                      <Change key={i}>
                        <ChangeLabel>{c.label}</ChangeLabel>
                        <FromTo>
                          <Old>{c.from ?? "—"}</Old>
                          <Arrow>→</Arrow>
                          <New>{c.to ?? "—"}</New>
                          {c.delta !== null && Math.abs(c.delta) > 0.005 && (
                            <Delta $positive={c.delta > 0}>
                              {c.delta > 0 ? "+" : ""}
                              {`$${c.delta.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </Delta>
                          )}
                        </FromTo>
                      </Change>
                    ))}
                  </Changes>
                ) : (
                  <NoChange>No value change recorded.</NoChange>
                )}
              </Body>
            </Row>
          );
        })}
      </Timeline>
      )}

      {!collapsed && hiddenCount > 0 && (
        <ShowMore onClick={() => setExpanded(true)}>Show {hiddenCount} more</ShowMore>
      )}
      {!collapsed && expanded && entries.length > PAGE_SIZE && (
        <ShowMore onClick={() => setExpanded(false)}>Collapse</ShowMore>
      )}
    </Section>
  );
};

export default ActivityHistory;

const Section = styled.section`
  margin: 28px 0 20px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  padding: 18px 20px 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Toggle = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 120ms ease;

  &:hover {
    background: #f8fafc;
  }
`;

const Chevron = styled.span<{ $collapsed: boolean }>`
  font-size: 14px;
  line-height: 1;
  display: inline-block;
  transition: transform 150ms ease;
  transform: rotate(${(p) => (p.$collapsed ? "-90deg" : "0deg")});
`;

const CollapsedHint = styled.span`
  font-size: 12px;
  color: #64748b;
  margin-left: 4px;

  strong {
    color: #334155;
    font-weight: 600;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4a90e2;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const Count = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 2px 8px;
`;

const Refresh = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 12px;
`;

const Empty = styled.div`
  color: #64748b;
  font-size: 13px;
  padding: 10px 4px 14px;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #f1f5f9;

  &:first-child {
    border-top: none;
  }
`;

const AvatarColumn = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const BodyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

const Actor = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const OpBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 7px;
  border: 1px solid currentColor;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.7);
`;

const SourceChip = styled.span`
  font-size: 12px;
  color: #475569;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 2px 10px;
`;

const Spacer = styled.span`
  flex: 1;
`;

const When = styled.span`
  font-size: 12px;
  color: #64748b;
  cursor: help;
`;

const Changes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Change = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #334155;
`;

const ChangeLabel = styled.span`
  font-weight: 600;
  color: #475569;
  width: 50px;
`;

const FromTo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Old = styled.span`
  color: #64748b;
  text-decoration: line-through;
`;

const Arrow = styled.span`
  color: #94a3b8;
`;

const New = styled.span`
  color: #0f172a;
  font-weight: 600;
`;

const Delta = styled.span<{ $positive: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$positive ? "#15803d" : "#b91c1c")};
  background: ${(p) => (p.$positive ? "#dcfce7" : "#fee2e2")};
  border-radius: 4px;
  padding: 1px 6px;
`;

const NoChange = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
`;

const ShowMore = styled.button`
  margin: 8px 0 12px;
  background: none;
  border: none;
  color: #2563eb;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
